import React, { useCallback, useEffect, useRef } from 'react'
import { useState } from 'react'
import { useNavigate } from "react-router-dom"
import moment from 'moment';
import { range } from 'lodash';
import { Button, Col, Form, Input, Row, Select, Space, Modal, Switch, DatePicker, message, UploadFile, Spin, Checkbox } from 'antd'
import styled from 'styled-components'
import axios from 'axios';
import jwt_decode from "jwt-decode";
import SearchModal from '@/modules/monetisation/search/search-modal-puja'
import S3Upload, { AllowedFormats } from '@a4b/ui/src/modules/monetisation/puja/molecules/S3Upload'
import { HubListItem } from '@a4b/api/src/modules/Monetisation/Puja/types'
import {CloseCircleOutlined, CopyOutlined, DeleteOutlined, FilterOutlined, PlusCircleOutlined} from '@ant-design/icons';
import { useAppContext } from '@/components/AppContext'
import FilterForm from '../FilterForm'
import usePujaStore from '@/modules/monetisation/puja/stores/puja-store'
import { getMonetisationPujaApi } from '@/utils/helper'
import useFetch from '@/hooks/useFetch'
import { PUJA_PRESIGN_URL, YODA_CDN_URL } from '@/utils/constants'
import SelectPickupAddress from '../SelectPickupAddress'
import { ONLY_ALPHA_NUMARIC_RULE_MAX_10_LEN } from '@/utils/form.validation'
import { getStatesByLanguage, StateOption } from './../../utils/constants'

import TempleConfigDrawer from './TempleConfigDrawer'
import TempleNameSuggestions from './TempleNameSuggestions'

const { Option } = Select
const { TextArea } = Input
interface Props {
  form: any
  initialStoreData: any
  currentStep: number
  isEditJourney: boolean
  slugState: boolean
  nextHandler: () => void
  previousHandler: () => void
  submitFormHandler: () => void
  onValuesChange: (values: any) => void
  handleCloneModal: () => void
}

const LabelRightAlign = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding-top: 10px;
`
const tags: any = {
  en: ['Festival', 'Kundali dosha', 'Business & Finance', 'Marriage & Love', 'Career'],
  hi: ['त्योहार', 'कुंडली दोष', 'धन एवं व्यापार', 'प्यार एवं विवाह', 'करियर']
}

// Helper function to convert base64 data URL to a File object
const dataURLtoFile = (dataurl: string, filename: string): File => {
  const arr = dataurl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  if (!mimeMatch) {
    throw new Error('Invalid data URL');
  }
  const mime = mimeMatch[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};


const StoreForm: React.FC<Props> = ({
  form,
  initialStoreData,
  previousHandler,
  nextHandler,
  submitFormHandler,
  currentStep,
  isEditJourney,
  onValuesChange,
  slugState,
  handleCloneModal,
}) => {
  const { variant } = usePujaStore()
  const { networkInstance, app, languageState } = useAppContext()
  const monetisationPujaApi = getMonetisationPujaApi(app, networkInstance)
  const navigate = useNavigate()

  // Get states based on current language
  const stateOptions = getStatesByLanguage(languageState?.monetisationLanguage)

  // Handle state and place_of_supply synchronization and language changes
  useEffect(() => {
    const currentState = form.getFieldValue('state');
    const currentPlaceOfSupply = form.getFieldValue('place_of_supply');

    if (stateOptions.length > 0) {
      // Case 1: State name exists but place_of_supply doesn't - set place_of_supply
      if (currentState && !currentPlaceOfSupply) {
        const selectedState = stateOptions.find(state => state.name === currentState);
        if (selectedState) {
          form.setFieldValue('place_of_supply', selectedState.code);
        }
      }

      // Case 2: place_of_supply exists but state name doesn't - set state name based on current language
      else if (currentPlaceOfSupply && !currentState) {
        const selectedState = stateOptions.find(state => state.code.toLowerCase() === currentPlaceOfSupply.toLowerCase());
        if (selectedState) {
          form.setFieldValue('state', selectedState.name);
        }
      }

      // Case 3: Both exist - language change scenario - update state name to current language
      else if (currentState && currentPlaceOfSupply) {
        const selectedState = stateOptions.find(state => state.code.toLowerCase() === currentPlaceOfSupply.toLowerCase());
        if (selectedState && selectedState.name !== currentState) {
          // Language has changed, update state name to match current language
          form.setFieldValue('state', selectedState.name);
        }
      }
    }
  }, [form, stateOptions, languageState?.monetisationLanguage]);

  useEffect(() => {
    if (!isEditJourney && form && initialStoreData === undefined) {
      const currentInfoBullets = form.getFieldValue('info_bullets');
      if (!currentInfoBullets || (Array.isArray(currentInfoBullets) && currentInfoBullets.length === 0)) {
        form.setFieldValue('info_bullets', []);
      }
    }
  }, [isEditJourney, initialStoreData, form]);

  useEffect(() => {

    if (isEditJourney && initialStoreData?.info_bullets && initialStoreData.info_bullets.length > 0) {
      form.setFieldValue('info_bullets', initialStoreData.info_bullets);
    }
  }, [isEditJourney, initialStoreData, form])

  const [visible, setVisible] = useState<boolean>(false)
  const [isTempleConfigVisible, setIsTempleConfigVisible] =
    useState<boolean>(false)
  const [showFilters, setShowFilters] = useState<boolean>(false)
  const [isSearchModalVisible, setIsSearchModalVisible] = useState<boolean>(false)

  const fetchHubList = useCallback(async () => {
    try {
      const hubListData = await monetisationPujaApi.hub.getHubList({ limit: 100, offset: 0 });
      return {
        data: hubListData?.data?.data?.hubs
      }
    } catch (error) {
      console.log("Error: ", error)
      return {
        err: "Failed to fetch adresses",
      }
    }
  }, [monetisationPujaApi.hub])

  const { data: hubList, error: hublistError, loading: isLoadingHublist } = useFetch(fetchHubList)

  const handleShowFilters = () => {
    setShowFilters((prevState) => !prevState)
  }

  const handleSearchModalClose = () => {
    // form.resetFields(["meta_data", "search_metadata"]);
    setIsSearchModalVisible(false);
  };

  const handleSearchModalSave = () => {
    setIsSearchModalVisible(false);
  };
  const showMetaDataModal = () => {
    setIsSearchModalVisible(true)
  }

  const videoRef = useRef<HTMLVideoElement>(null);
  const [snapshots, setSnapshots] = useState<string[]>([]);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);


  const [isGeneratingSnapshots, setIsGeneratingSnapshots] = useState<boolean>(false);
  const [uploadingThumbnails, setUploadingThumbnails] = useState<{ [key: number]: boolean }>({});

  // ✅ CORRECTED HELPER FUNCTION
  const uploadSnapshot = async (snapshotDataUrl: string, index: number) => {
    if (!snapshotDataUrl) return;

    setUploadingThumbnails(prev => ({ ...prev, [index]: true }));

    const fileName = `snapshot_${Date.now()}.jpeg`;
    const file = dataURLtoFile(snapshotDataUrl, fileName);
    const actionUrl = `${PUJA_PRESIGN_URL}${fileName}`;

    try {
      const token = JSON.parse(localStorage.getItem('userinfo') || '{}')?.access_token;
      const jwtRes: any = token ? jwt_decode(token) : {};
      const headers: any = {
        'x-afb-app-ver': '1.0.0',
        'x-afb-device-id': window.navigator.userAgent,
        'x-afb-platform': 'web',
        'Authorization': 'Bearer ' + token,
      };
      if (jwtRes.sub) {
        headers['X-AFB-R-UID'] = jwtRes.sub;
      }

      const presignedResponse = await axios.get(actionUrl, { headers });
      const presignedUrl = presignedResponse.data?.data?.url;

      if (!presignedUrl) {
        throw new Error("Failed to get a presigned URL.");
      }

      await axios.put(presignedUrl, file, { headers: { 'Content-Type': file.type } });

      const finalUrl = `${YODA_CDN_URL}/${fileName}`;

      // Use setFieldValue with the correct nested path
      const thumbnailPath = ['cover_media_video', 'media_thumbnails', index, 'media_url'];
      form.setFieldValue(thumbnailPath, [finalUrl]); // S3Upload expects an array

      message.success(`Thumbnail ${index + 1} uploaded successfully!`);
    } catch (error) {
      console.error('Failed to upload snapshot:', error);
      message.error(`Failed to upload thumbnail ${index + 1}.`);
    } finally {
      setUploadingThumbnails(prev => ({ ...prev, [index]: false }));
    }
  };


  // Modified video URL effect to auto-generate snapshots
  useEffect(() => {
    const mediaUrl = form.getFieldValue(['cover_media_video', 'media_url']);
    if (mediaUrl && mediaUrl.length > 0) {
      const currentVideoUrl = Array.isArray(mediaUrl[0]) ? mediaUrl[0][0] : mediaUrl[0];
      setVideoUrl(currentVideoUrl);
      // Auto-generate snapshots when video is uploaded
      setTimeout(() => {
        generateSnapshots();
      }, 1000); // Small delay to ensure video is loaded
    }
  }, [form.getFieldValue(['cover_media_video', 'media_url'])]);


  const generateSnapshots = async () => {
    if (!videoRef.current || !videoUrl) {
      return;
    }

    setIsGeneratingSnapshots(true);
    const video = videoRef.current;

    const captureSnapshot = (time: number): Promise<string> => {
      return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');

        const onError = () => {
          video.removeEventListener('seeked', seekHandler);
          video.removeEventListener('error', onError);
          reject(new Error(`Failed to seek to ${time}s.`));
        }

        const seekHandler = () => {
          video.removeEventListener('seeked', seekHandler);
          video.removeEventListener('error', onError);

          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve('');

          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg'));
        };

        video.addEventListener('seeked', seekHandler);
        video.addEventListener('error', onError)
        video.currentTime = time;
      });
    };

    const handleLoadedMetadata = async () => {
      try {
        const duration = video.duration;
        // 1. Set the desired timestamps in seconds.
        const targetTimestamps = [1, 5, 10];

        // 2. Determine which timestamps are possible based on the video's duration.
        const validTimestamps = targetTimestamps.filter(time => time < duration);

        // 3. Inform the user if the video is too short for all 3 snapshots.
        if (duration < 10) {
          if (validTimestamps.length > 0) {
            message.info(`Video is shorter than 10 seconds. Generated ${validTimestamps.length} snapshot(s).`);
          } else {
            message.info('Video is too short to generate any snapshots.');
          }
        }

        const imgs: string[] = [];
        // 4. Loop through only the valid timestamps to capture snapshots.
        for (const time of validTimestamps) {
          const imgData = await captureSnapshot(time);
          if (imgData) imgs.push(imgData);
        }

        setSnapshots(imgs);

        // Automatically upload the first snapshot if it exists.
        if (imgs.length > 0) {
          const firstThumbnailPath = ['cover_media_video', 'media_thumbnails', 0, 'media_url'];
          const firstThumbnailValue = form.getFieldValue(firstThumbnailPath);

          if (!firstThumbnailValue || firstThumbnailValue.length === 0) {
            await uploadSnapshot(imgs[0], 0);
          }
        }

      } catch (error) {
        console.error('Error generating snapshots:', error);
        message.error('Failed to generate snapshots. The video may not be accessible.');
      } finally {
        setIsGeneratingSnapshots(false);
        // Clean up event listener
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      }
    };

    // Add a one-time event listener for when the video metadata is loaded.
    video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
    video.load();
  };
  const handleThumbnailDrop = async (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    const snapshotDataUrl = e.dataTransfer.getData('text/plain');
    await uploadSnapshot(snapshotDataUrl, index);
  };


  return (
    <div>
      <Form
        form={form}
        layout='vertical'
        name='create store'
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        initialValues={{ remember: true }}
        onValuesChange={onValuesChange}
      >
        <Col span={24}>
          <Row>
            <Col span={8}>
              {isEditJourney && <Form.Item
                  label='Store Id'
                  name='store_id'
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                >
                  <Input style={{ color: 'black' }} disabled></Input>
              </Form.Item>}
              <Form.Item
                label='Select Hub'
                name='hub_id'
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[{ required: true, message: 'Please select hub name!' }]}
              >
                <Select placeholder='Select hub name' loading={isLoadingHublist} disabled={!!variant}>
                  {hubList?.map((hub: HubListItem) => (
                    <Option value={hub.id} key={hub.id}>
                      {hub.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label='Name of Store(Old format)'
                name='title'
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: 'Please input name of the temple!' },
                ]}
              >
                <Input placeholder='Enter temple name' />
              </Form.Item>
              <Form.Item
                label='Specificity'
                name='pooja_specificity'
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: 'Please Puja Tithi or Specificity' },
                ]}
              >
                <Input placeholder='Enter Puja Tithi or Specificity' />
              </Form.Item>
              <Form.Item
                label='Name of Store(New format)'
                name='pooja_title'
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: 'Please Title' },
                ]}
              >
                <Input placeholder='Enter Puja Title' />
              </Form.Item>
              <Form.Item
                label='Use case'
                name='pooja_usecase'
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: 'Please Puja Use case' },
                ]}
              >
                <Input placeholder='Enter Puja Use case' />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.service_type !== currentValues.service_type
                }
              >
                {({ getFieldValue }) => (
                  getFieldValue("service_type") === "pooja" &&
                  (!slugState ? (<>
                      <Form.Item
                        label='Slug'
                        name='slug'
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        rules={[
                          { required: true, message: 'Please input slug' },
                        ]}
                      >
                      <Input disabled={!!variant} placeholder='Enter Slug' onInput={(e: any) => e.target.value = e.target.value.toLowerCase()} />
                      </Form.Item>
                  </>) : (<>
                      <Form.Item
                        label='Slug'
                        name='slug'
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                      validateStatus="error"
                      help="Enter valid Slug"
                        rules={[
                          { required: true, message: 'Please input slug' },
                        ]}
                      >
                      <Input disabled={!!variant} placeholder='Enter Slug' onInput={(e: any) => e.target.value = e.target.value.toLowerCase()} />
                      </Form.Item>
                  </>))

                )}
              </Form.Item>
              <Form.Item
                label='Short Description'
                name='short_description'
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: 'Please input short description!' },
                ]}
              >
                <TextArea placeholder='Enter short description' />
              </Form.Item>
              <Form.Item
                label='Long Description'
                name='long_description'
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: 'Please input long description!' },
                ]}
              >
                <TextArea placeholder='Enter long description' />
              </Form.Item>

              <Row justify='space-between' style={{ width: '100%', marginTop: '10px' }}>
                <Form.Item
                  label='City'
                  name='city'
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  rules={[{ required: true, message: 'Please input city name!' }]}
                  style={{ width: '48%' }}
                >
                  <Input placeholder='Enter city name' />
                </Form.Item>

                <Form.Item
                  label='State'
                  name='state'
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  rules={[
                    { required: true, message: 'Please select state!' },
                    {
                      validator: async (_, value) => {
                        if (value && !stateOptions.some(state => state.name === value)) {
                          message.error('Please select any state from the list');
                          return Promise.reject('Please select a valid state from the list');
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                  style={{ width: '48%' }}
                >
                  <Select
                    placeholder='Select state'
                    showSearch
                    filterOption={(input, option) => {
                      const label = option?.label || '';
                      return String(label).toLowerCase().includes(input.toLowerCase());
                    }}
                    onSelect={(value: string) => {
                      // Additional validation on select
                      const selectedState = stateOptions.find(state => state.name === value);
                      if (!selectedState) {
                        message.error('Please select any state from the list');
                        form.setFieldValue('state', undefined);
                      } else {
                        // Set place of supply to the selected state code
                        form.setFieldValue('place_of_supply', selectedState.code);
                      }
                    }}
                  >
                    {stateOptions.map((state: StateOption) => (
                      <Option key={state.code} value={state.name} label={state.name}>
                        {state.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label='place of supply'
                  name='place_of_supply'
                  style={{ display: 'none' }}
                >
                  <Input />
                </Form.Item>
                {/* Hidden fields for temple config */}
                <Form.Item
                  name='multi_day'
                  style={{ display: 'none' }}
                  valuePropName='checked'
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  name='multi_temple'
                  style={{ display: 'none' }}
                  valuePropName='checked'
                >
                  <Switch />
                </Form.Item>
                <Form.Item name='executions' style={{ display: 'none' }}>
                  <Input />
                </Form.Item>
              </Row>
              <Form.Item
                label={`Temple & Pandit Details`}
                name='location_details'
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    required: false,
                    message: 'Please enter location details',
                  },
                ]}
              >
                <Button style={{ width: '100%', background: '#1890ff', color: 'white' }} onClick={() => setVisible(true)}>
                  Add temple and Pandit Details
                </Button>
                <Modal
                  title="Location details"
                  visible={visible}
                  onOk={() => setVisible(false)}
                  onCancel={() => setVisible(false)}
                  width="45vw"
                  style={{ marginTop: '-95px' }}
                >
                  <Row style={{ display: "flex", width: "40%", justifyContent: "space-between" }}>
                    <Form.Item
                      labelCol={{ span: 18 }}
                      wrapperCol={{ span: 18 }}
                      label="Enable Location "
                      name={['location_details', 'show_location_details']}
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Row>

                  <h3>Temple details</h3>
                  <Row>
                    <Col span={12}>
                      <TempleNameSuggestions />
                      <Form.Item
                        name={["location_details", "temple_details", "description"]}
                        label="Description"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        rules={[{ required: false, message: 'Please input your Temple description' }]}
                      >
                        <TextArea />
                      </Form.Item>
                    </Col>
                    <Col span={12} style={{ paddingLeft: '20px' }}>
                      <Form.Item
                        name={["location_details", "temple_details", "location"]}
                        label="Location"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        rules={[{ required: false, message: 'Please input your Temple location' }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        label={`Temple image`}
                        name={["location_details", "temple_details", "image"]}
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        rules={[
                          {
                            required: false,
                            min: 0,
                            max: 1,
                            validator: async (rules: any, values: any) => {
                              if (values?.length < rules.min) {
                                return Promise.reject('Please upload Temple Image')
                              }
                            },
                          },
                        ]}
                        valuePropName='fileList'
                      >
                        <S3Upload
                          cdnUrl={YODA_CDN_URL}
                          preSignUrl={PUJA_PRESIGN_URL}
                          min={1}
                          max={1}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  {/* <Divider /> */}
                  <h3>Pandit details</h3>
                  <Row>
                    <Col span={12}>

                      <Form.Item
                        name={["location_details", "pandit_details", "name"]}
                        label="Name"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        rules={[{ required: false, message: 'Please input your Pandit name' }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        name={["location_details", "pandit_details", "description"]}
                        label="Description"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        rules={[{ required: false, message: 'Please input your Pandit description' }]}
                      >
                        <TextArea />
                      </Form.Item>

                    </Col>
                    <Col span={12} style={{ paddingLeft: '20px' }}>
                      <Form.Item
                        name={["location_details", "pandit_details", "location"]}
                        label="Location"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        rules={[{ required: false, message: 'Please input your Pandit location' }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        label={`Pandit image`}
                        name={["location_details", "pandit_details", "image"]}
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        rules={[
                          {
                            required: false,
                            min: 0,
                            max: 1,
                            validator: async (rules: any, values: any) => {
                              if (values?.length < rules.min) {
                                return Promise.reject('Please upload Pandit Image')
                              }
                            },
                          },
                        ]}
                        valuePropName='fileList'
                      >
                        <S3Upload
                          cdnUrl={YODA_CDN_URL}
                          preSignUrl={PUJA_PRESIGN_URL}
                          min={1}
                          max={1}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Modal>
              </Form.Item>
            </Col >
            {/* <Col span={1}></Col> */}
            <Col style={{ marginLeft: '25px' }} span={8}>
              <Form.Item
                label='Puja Image L1'
                style={{ marginBottom: '0px' }}
              >
                <div style={{
                    border: '1px dashed #e0dcdc',
                    marginRight: '0px',
                    marginBottom: '3px',
                    padding: '10px',
                }} title='Order Intro Media'>
                  <Row justify="space-between" style={{ width: '100%' }}>
                    <Form.Item
                      label={`Media type`}
                      name='coverMediaType'
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      rules={[
                        {
                          required: true,
                          message: 'Please select cover media type!',
                        },
                      ]}
                      style={{}}
                    >
                      <Select style={{ width: 100 }} placeholder='Type'>
                        <Option value='lottie'>Lottie</Option>
                        <Option value='image'>Image</Option>
                        <Option value='video'>Video</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label={`Media`}
                      name='media_url'
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      rules={[
                        {
                          min: 1,
                          max: 1,
                          validator: async (rules: any, values: any) => {
                            if (values?.length < rules.min) {
                              return Promise.reject('Please upload Cover media Images')
                            }
                          },
                        },
                      ]}
                      valuePropName='fileList'
                    >
                      <S3Upload
                        cdnUrl={YODA_CDN_URL}
                        preSignUrl={PUJA_PRESIGN_URL}
                        min={1}
                        max={1}
                      />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.coverMediaType !== currentValues.coverMediaType
                      }
                    >
                      {({ getFieldValue }) => (
                        getFieldValue("coverMediaType") === "video" &&
                          <Form.Item
                            label={`Thumbnail`}
                            name='thumbnail'
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 24 }}
                            rules={[
                              {
                                min: 1,
                                max: 1,
                                validator: async (rules: any, values: any) => {
                                  if (values?.length < rules.min) {
                                  return Promise.reject('Please upload Thumbnail Image')
                                  }
                                },
                              },
                            ]}
                            valuePropName='fileList'
                          >
                            <S3Upload
                              cdnUrl={YODA_CDN_URL}
                              preSignUrl={PUJA_PRESIGN_URL}
                              min={0}
                              max={1}
                            />
                          </Form.Item>
                      )}
                    </Form.Item>
                  </Row>
                </div>
              </Form.Item>
              <Form.Item
                label='Video Feed Card'
                style={{ marginBottom: '0px' }}
              >
                <div style={{
                    border: '1px dashed #e0dcdc',
                    marginRight: '0px',
                    marginBottom: '3px',
                    padding: '10px',
                }}>
                  <Form.Item
                    label={`Media`}
                    name={['cover_media_video', 'media_url']}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                    // rules={[
                    //   {
                    //     message: 'Please upload video',
                    //     required: true
                    //   },
                    // ]}
                    valuePropName='fileList'
                  >
                    <S3Upload
                      cdnUrl={YODA_CDN_URL}
                      preSignUrl={PUJA_PRESIGN_URL}
                      min={1}
                      max={1}
                      allowedFileExtension={[AllowedFormats.MP4]}
                      onChange={(urls) => {
                        const newVideoUrl = urls[0] || null;
                        setVideoUrl(newVideoUrl);
                        setSnapshots([]);
                        setIsGeneratingSnapshots(false);
                        form.setFieldsValue({
                          cover_media_video: {
                            media_thumbnails: [
                              { media_url: [] },
                            ]
                          }
                        });
                      }}
                      maxSizeInKiloBytes={250000}
                      isM3U8
                    />
                  </Form.Item>
                  <Form.Item
                    label={`Play Audio`}
                    name={['cover_media_video', 'play_audio']}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                    initialValue={true}
                    valuePropName="checked"

                  >
                    <Checkbox />
                  </Form.Item>
                  {/* Hidden video element for snapshot generation */}
                  {videoUrl && (
                    <video
                      ref={videoRef}
                      src={videoUrl}
                      style={{ display: 'none' }}
                      crossOrigin="anonymous"
                    />
                  )}

                  {/* Loading state for snapshot generation */}
                  {isGeneratingSnapshots && (
                    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                      <Spin /> <span>Generating snapshots...</span>
                    </div>
                  )}

                  {/* Display generated snapshots */}
                  {snapshots.length > 0 && !isGeneratingSnapshots && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4>Generated Snapshots (Drag to thumbnails below)</h4>
                      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        {snapshots.map((src, i) => (
                          <img
                            key={i}
                            src={src}
                            alt={`Snapshot ${i + 1}`}
                            style={{
                              width: '100px',
                              height: '75px',
                              objectFit: 'cover',
                              cursor: 'move',
                              border: '2px solid #d9d9d9',
                              borderRadius: '4px'
                            }}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', src);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}


                  <h4>Thumbnails</h4>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <Form.Item noStyle shouldUpdate>
                      {(formInstance) => {
                        const videoUrl = formInstance.getFieldValue(['cover_media_video', 'media_url']);
                        const isVideoUploaded = videoUrl && videoUrl.length > 0;

                        return [0].map((index) => {
                          const fieldValue = formInstance.getFieldValue(['cover_media_video', 'media_thumbnails', index, 'media_url']);
                          const dynamicKey = Array.isArray(fieldValue) ? fieldValue[0] : undefined;

                          return (
                            <div
                              key={index}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => handleThumbnailDrop(e, index)}
                              style={{
                                opacity: isVideoUploaded ? 1 : 0.5,
                                pointerEvents: isVideoUploaded ? 'auto' : 'none'
                              }}
                            >
                              <Form.Item
                                name={['cover_media_video', 'media_thumbnails', index, 'media_url']}
                                valuePropName='fileList'
                                noStyle
                                rules={[
                                  {
                                    required: isVideoUploaded,
                                    message: "Please upload a thumbnail since you've uploaded a video"
                                  }
                                ]}
                              >
                                <S3Upload
                                  key={dynamicKey}
                                  cdnUrl={YODA_CDN_URL}
                                  preSignUrl={PUJA_PRESIGN_URL}
                                  min={0}
                                  max={1}
                                  disabled={!isVideoUploaded}
                                />
                              </Form.Item>
                              {!isVideoUploaded && (
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    color: '#999',
                                    fontSize: '12px',
                                    textAlign: 'center',
                                  pointerEvents: 'none'
                                }}>
                                  Upload video first
                                </div>
                              )}
                            </div>
                          );
                        });
                      }}
                    </Form.Item>
                  </div>

                </div>
              </Form.Item>
              <Form.Item
                label='Puja Images L2'
                style={{ marginTop: '20px' }}
              >
                <div style={{
                    border: '1px dashed #e0dcdc',
                    marginRight: '0px',
                    paddingTop: '10px',
                    paddingLeft: '10px',
                    paddingRight: '10px',
                }} title='Order Intro Media'>
                  <Row justify='space-between'>
                    <Form.Item
                      label={`Temple listing images`}
                      name='info_media_image'
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      valuePropName='fileList'
                      rules={[
                        {
                          min: 1,
                          max: 10,
                          validator: async (rules: any, values: any) => {
                            if (values?.length < rules.min) {
                              return Promise.reject('Please upload Temple listing Images')
                            }
                          },
                        },
                      ]}
                    >
                      <S3Upload cdnUrl={YODA_CDN_URL} preSignUrl={PUJA_PRESIGN_URL} min={1} max={10} multiple={true} />
                    </Form.Item>
                    <Form.Item
                      label={`Temple listing video`}
                      name='info_media_video'
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      valuePropName='fileList'
                      extra='Upload video'
                      rules={[
                        {
                          min: 0,
                          max: 1,
                          validator: async (rules: any, values: any) => {
                            if (values?.length < rules.min) {
                              return Promise.reject('Please upload Cover media video')
                            }
                          },
                        },
                      ]}
                    >
                      <S3Upload cdnUrl={YODA_CDN_URL} preSignUrl={PUJA_PRESIGN_URL} min={0} max={1} />
                    </Form.Item>
                  </Row>
                </div>
              </Form.Item>

              <Form.Item
                style={{ width: '100%' }}
                name='schedule_date'
                label='Schedule Date'
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    required: false,
                    message: 'Pick Date',
                  },
                ]}
              >
                <DatePicker disabled={!!variant} style={{ width: '100%' }} showTime />
              </Form.Item>
              <Form.Item
                style={{ width: '100%' }}
                name='delivery_date'
                label='Delivery Date'
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    required: true,
                    message: 'Please select the delivery date',
                  },
                ]}
              >
                <DatePicker
                  disabled={!!variant}
                  style={{ width: '100%' }}
                  showTime
                  disabledDate={(current) => current && current <= moment().startOf('day')}
                />
              </Form.Item>

              <Form.Item
                label='Lunar calender date'
                name='hindi_calendar_date'
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: false, message: 'Please enter Hindi calendar date' },
                ]}
              >
                <Input
                  disabled={!!variant}
                  placeholder='Enter Hindi calendar date' />
              </Form.Item>

              <Row style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", marginTop: '10px', }}>
                <Form.Item
                  label={`Icon URL`}
                  name='icon_url'
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  rules={[
                    {
                      min: 0,
                      max: 1,
                      validator: async (rules: any, values: any) => {
                        if (values?.length < rules.min) {
                          return Promise.reject('Please upload icon')
                        }
                      },
                    },
                  ]}
                  valuePropName='fileList'
                >
                  <S3Upload cdnUrl={YODA_CDN_URL} preSignUrl={PUJA_PRESIGN_URL} min={0} max={1} />
                </Form.Item>

                <Form.Item
                  label={`Service Type`}
                  name='service_type'
                  labelCol={{ span: 22 }}
                  wrapperCol={{ span: 22 }}
                  rules={[
                    {
                      required: false,
                      message: 'Please select service type!',
                    },
                  ]}
                >
                  <Select style={{ width: 120 }} placeholder='Service Type' disabled>
                    <Option value='pooja'>Pooja</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  label="Show Reorder Card(L2)"
                  name='show_reorder_card_l2'
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Row>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.service_type !== currentValues.service_type
                }
              >
                {({ getFieldValue }) => (
                  getFieldValue("service_type") === "pooja" &&
                    <Form.Item
                      label={`Astro puja list`}
                      name='astro_pooja_list'
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      rules={[
                        {
                          required: false,
                          message: 'Please select Astro puja list!',
                        },
                      ]}
                    >
                    <Select placeholder='Astro Puja list' mode="multiple">
                        <Option value='KAL_SARPA'>KAL SARPA</Option>
                        <Option value='PITRI_DOSHA'>PITRI DOSHA</Option>
                        <Option value='NAKSHATRA_PUJA'>NAKSHATRA PUJA</Option>
                        <Option value='WELCOME_PUJA'>WELCOME PUJA</Option>
                        <Option value='MANGAL_DOSHA'>MANGAL DOSHA</Option>
                      <Option value='GURU_CHANDAL_DOSHA'>GURU CHANDAL DOSHA</Option>
                        <Option value='GRAHAN_DOSHA'>GRAHAN DOSHA</Option>
                        <Option value='GANDMOOL_DOSHA'>GANDMOOL DOSHA</Option>
                        <Option value='SHANI_DOSHA'>SHANI DOSHA</Option>
                        <Option value='RAHU_DOSHA'>RAHU DOSHA</Option>
                        <Option value='NAVGRAHA_DOSHA'>NAVGRAHA DOSHA</Option>
                      </Select>
                    </Form.Item>
                )}
              </Form.Item>
              {/* Add the search criteria button */}
              <Form.Item
                label="SearchMetadata"
                name={['search_metadata']}  // Corrected name path for nested fields
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <Button
                  style={{ width: 150, background: '#1890ff', color: 'white' }}
                  onClick={showMetaDataModal}
                >
                  Search Metadata
                </Button>

                <SearchModal
                  visible={isSearchModalVisible}
                  onClose={handleSearchModalClose}
                  onSave={handleSearchModalSave}
                  form={form}
                />
              </Form.Item>
              {/* <Form.Item
                label={`Tags`}
                name='tags'
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    required: false,
                    message: 'Please select Tags!',
                  },
                ]}
              >
                <Select placeholder='Tags' mode="multiple">
                  {tags?.[languageState?.monetisationLanguage]?.map((tag: string) => (
                    <Option key={tag} value={tag}>{tag}</Option>
                  ))}
                </Select>
              </Form.Item> */}
            </Col >
            {/* <Col span={1}></Col> */}
            <Col style={{ marginLeft: '25px' }} span={7}>
              <div style={{ marginBottom: '20px' }}>
                <Button
                  type='primary'
                  onClick={() => setIsTempleConfigVisible(true)}
                  block
                >
                  MTMD Config
                </Button>
                <TempleConfigDrawer
                  visible={isTempleConfigVisible}
                  onClose={() => setIsTempleConfigVisible(false)}
                  form={form}
                  hubList={hubList}
                  initialStoreData={initialStoreData}
                  isEdit={isEditJourney}
                />
              </div>
              <Form.Item
                label='Closed Cover Media'
                style={{ marginBottom: '0px' }}
              >
                <div style={{
                    border: '1px dashed #e0dcdc',
                    marginRight: '0px',
                    marginBottom: '3px',
                    padding: '10px',
                }} title='Order Intro Media'>
                  <Row justify='space-between'>
                    <Form.Item
                      label={`Media type`}
                      name='closedCoverMediaType'
                      labelCol={{ span: 20 }}
                      wrapperCol={{ span: 24 }}
                      rules={[
                        {
                          required: true,
                          message: 'Please select closed cover media type!',
                        },
                      ]}
                    >
                      <Select style={{ width: 100 }} placeholder='Type'>
                        <Option value='lottie'>Lottie</Option>
                        <Option value='image'>Image</Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      label={`Media`}
                      name='closed_media_url'
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      rules={[
                        {
                          min: 1,
                          max: 1,
                          validator: async (rules: any, values: any) => {
                            if (values?.length < rules.min) {
                              return Promise.reject('Please upload Closed Cover Media')
                            }
                          },
                        },
                      ]}
                      valuePropName='fileList'
                    >
                      <S3Upload
                        cdnUrl={YODA_CDN_URL}
                        preSignUrl={PUJA_PRESIGN_URL}
                        min={1}
                        max={1}
                      />
                    </Form.Item>
                  </Row>
                </div>
              </Form.Item>

              <Form.Item
                label='Closed Temple Media'
                style={{ marginTop: '20px' }}
              >
                <div style={{
                    border: '1px dashed #e0dcdc',
                    marginRight: '0px',
                    marginBottom: '3px',
                    padding: '10px',
                }} title='Order Intro Media'>
                  <Row justify='space-between'>
                    <Form.Item
                      label={`Images`}
                      name='closed_info_media_image'
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      valuePropName='fileList'
                      extra='Upload images'
                      rules={[
                        {
                          min: 1,
                          max: 4,
                          validator: async (rules: any, values: any) => {
                            if (values?.length < rules.min) {
                              return Promise.reject('Please upload closed Temple listing Images')
                            }
                          },
                        },
                      ]}
                    >
                      <S3Upload cdnUrl={YODA_CDN_URL} preSignUrl={PUJA_PRESIGN_URL} min={1} max={4} />
                    </Form.Item>

                    <Form.Item
                      label={`Videos`}
                      name='closed_info_media_video'
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      valuePropName='fileList'
                      extra='Upload video'
                      rules={[
                        {
                          min: 0,
                          max: 1,
                          validator: async (rules: any, values: any) => {
                            if (values?.length < rules.min) {
                              return Promise.reject('Please upload Closed Cover media video')
                            }
                          },
                        },
                      ]}
                    >
                      <S3Upload cdnUrl={YODA_CDN_URL} preSignUrl={PUJA_PRESIGN_URL} min={0} max={1} />
                    </Form.Item>
                  </Row>
                </div>
              </Form.Item>
              <Row justify='space-between'>
                <Col span={24}>
                  <Form.Item
                    label='Whatsapp Media'
                    style={{ marginTop: '0px' }}
                  >
                    <div style={{
                        border: '1px dashed #e0dcdc',
                        marginRight: '0px',
                        marginBottom: '3px',
                        padding: '10px',
                    }} title='Order Intro Media'>
                      <Row justify='space-between'>
                        <Form.Item
                          label='Media type'
                          name='whatsappMediaType'
                          rules={[{ required: false, message: 'Please select media type!' }]}
                        >
                          <Select style={{ width: 100 }} placeholder='Type'>
                            <Option value='video'>Video</Option>
                            <Option value='image'>Image</Option>
                          </Select>
                        </Form.Item>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.whatsappMediaType !== currentValues.whatsappMediaType
                          }
                        >
                          {({ getFieldValue }) => (
                            getFieldValue("whatsappMediaType") === "video" && <Row>
                                <Col span={12}>
                                  <Form.Item
                                    label='Media'
                                    name='whatsapp_share_media'
                                    labelCol={{ span: 24 }}
                                    wrapperCol={{ span: 24 }}
                                    valuePropName='fileList'
                                    rules={[
                                      {
                                        min: 0,
                                        max: 1,
                                      validator: async (rules: any, values: any) => {
                                          if (values?.length < rules.min) {
                                          return Promise.reject('Please upload MP4 media url')
                                          }
                                        },
                                      },
                                    ]}
                                  >
                                    <S3Upload
                                      cdnUrl={YODA_CDN_URL}
                                      preSignUrl={PUJA_PRESIGN_URL}
                                    allowedFileExtension={[AllowedFormats.MP4]}
                                      min={0}
                                      max={1}
                                    />
                                  </Form.Item>
                                </Col>

                              </Row>

                          )}
                        </Form.Item>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.whatsappMediaType !== currentValues.whatsappMediaType
                          }
                        >
                          {({ getFieldValue }) => (
                            getFieldValue("whatsappMediaType") === "image" && <Row>
                                <Col span={12}>
                                  <Form.Item
                                    label='Media'
                                    name='whatsapp_share_media'
                                    labelCol={{ span: 24 }}
                                    wrapperCol={{ span: 24 }}
                                    valuePropName='fileList'
                                    rules={[
                                      {
                                        min: 0,
                                        max: 1,
                                      validator: async (rules: any, values: any) => {
                                          if (values?.length < rules.min) {
                                          return Promise.reject('Please upload image ')
                                          }
                                        },
                                      },
                                    ]}
                                  >
                                    <S3Upload
                                      cdnUrl={YODA_CDN_URL}
                                      preSignUrl={PUJA_PRESIGN_URL}
                                    allowedFileExtension={[AllowedFormats.PNG, AllowedFormats.JPEG,]}
                                      min={0}
                                      max={1}
                                    />
                                  </Form.Item>

                                </Col>
                              </Row>

                          )}
                        </Form.Item>
                      </Row>
                    </div></Form.Item>
                  <Row>
                    <Button
                      icon={<CopyOutlined />}
                      type='primary'
                      onClick={handleCloneModal}
                      style={{ marginTop: '0px', width: '100%' }}
                    >
                      Clone
                    </Button>
                  </Row>
                  {/* Pickup address select*/}
                  <div style={{ marginTop: '20px' }}>
                    <SelectPickupAddress formItemName='pickup_address' />
                  </div>
                  <Form.Item
                    label={`Sku code`}
                    name='sku_code'
                    labelCol={{ span: 28 }}
                    wrapperCol={{ span: 28 }}
                    style={{ flexBasis: '85%', marginBottom: '0px' }}
                    rules={[
                      {
                        required: true,
                        message: 'SKU Code is required',
                      },
                      ONLY_ALPHA_NUMARIC_RULE_MAX_10_LEN
                    ]}
                  >
                    <Input placeholder="Enter SKU Code" />
                  </Form.Item>
                </Col>
              </Row >
            </Col >
            <Col style={{ marginLeft: '25px' }} span={10}>
              <Col span={24} style={{ paddingTop: '15px' }}>
                      <Row style={{ border: '1px solid #e0dcdc', padding: '10px', borderRadius: '5px' }}>
                  <Col span={24}>
                              <h4 style={{ marginTop: 0, marginBottom: '15px' }}>Common Package Content</h4>


                              <Form.List name="info_bullets">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field, index) => (
                                              <Col key={field.key} span={24} style={{ paddingBottom: '10px' }}>
                              <div
                                style={{
                                  border: '1px dashed #e0dcdc',
                                  padding: '10px',
                                  borderRadius: '4px',
                                }}
                              >
                                                      <Row justify="space-between" align="top" style={{ width: '100%' }}>
                                  <Col span={22}>
                                    <Form.Item
                                      label={`Point ${index + 1}`}
                                      name={[field.name, 'text']}
                                      labelCol={{ span: 24 }}
                                      wrapperCol={{ span: 24 }}
                                      style={{ marginBottom: 0 }}
                                      rules={[
                                        {
                                          message: 'Enter Info bullet',
                                        },
                                        {
                                          validator: (_, value) => {
                                                                              if (!value) return Promise.resolve();
                                            if (value.length > 100) {
                                                                                  return Promise.reject('Maximum 100 characters allowed');
                                            }
                                                                              return Promise.resolve();
                                                                          }
                                                                      }
                                      ]}
                                    >
                                                                  <TextArea rows={2} placeholder="Enter Info bullet (max 100 characters)" />
                                    </Form.Item>
                                  </Col>
                                                          <Col span={2} style={{ display: 'flex', justifyContent: 'center', paddingTop: '40px' }}>
                                    <Button
                                      onClick={() => remove(field.name)}
                                                                  type="ghost"
                                      icon={<DeleteOutlined />}
                                                                  size="middle"
                                      danger
                                    />
                                  </Col>
                                </Row>
                              </div>
                            </Col>
                          ))}

                          <Col span={24} style={{ paddingTop: '10px' }}>
                            <Button
                              onClick={() => add()}
                                                  type="ghost"
                              icon={<PlusCircleOutlined />}
                                                  size="middle"
                            >
                              Add Bullet Point
                            </Button>
                          </Col>
                        </>
                      )}
                    </Form.List>
                  </Col>
                </Row>
              </Col>
            </Col>
          </Row >
        </Col >
        <Col span={24}>
          <Row>
            <Col span={11} style={{ marginTop: '20px' }}>
              <Form.Item
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                label="Enable Mantra Card"
                name={['enable_mantra_data']}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.enable_mantra_data !== currentValues.enable_mantra_data
                }
              >
                {({ getFieldValue }) =>

                  <Form.Item
                    label='Mantra Card'
                    rules={[{ required: true, message: 'Please select media type!' }]}
                  >
                    <div style={{ border: '1px dashed #e0dcdc', padding: '10px' }}>
                      <Form.Item
                        label='Title'
                        name={['mantra_data', 'title']}
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        rules={[
                          { required: getFieldValue("enable_mantra_data"), message: 'Please input Title' },
                        ]}
                      >
                        <Input placeholder='Enter Mantra Title' disabled={!getFieldValue("enable_mantra_data")} />
                      </Form.Item>
                      <Form.Item
                        label='Subtitle'
                        name={['mantra_data', 'subtitle']}
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        rules={[
                          { required: getFieldValue("enable_mantra_data"), message: 'Please input Subtitle' },
                        ]}
                      >
                        <Input placeholder='Enter Mantra Subtitle' disabled={!getFieldValue("enable_mantra_data")} />
                      </Form.Item>
                      <Form.Item
                        label='Deeplink'
                        name={['mantra_data', 'deeplink']}
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        rules={[
                          { required: getFieldValue("enable_mantra_data"), message: 'Please input deeplink' },
                        ]}
                      >
                        <Input placeholder='Enter Deeplink' disabled={!getFieldValue("enable_mantra_data")} />
                      </Form.Item>
                      <Form.Item
                        label='Icon'
                        name={['mantra_data', 'icon']}
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        valuePropName='fileList'
                        rules={[
                          {
                            min: getFieldValue("enable_mantra_data") ? 1 : 0,
                            max: 1,
                            validator: async (rules: any, values: any) => {
                              if (values?.length < rules.min) {
                                return Promise.reject('Please upload image ')
                              }
                            },
                          },
                        ]}
                      >
                        <S3Upload
                          cdnUrl={YODA_CDN_URL}
                          preSignUrl={PUJA_PRESIGN_URL}
                          min={getFieldValue("enable_mantra_data") ? 1 : 0}
                          max={1}
                          disabled={!getFieldValue("enable_mantra_data")}
                        />
                      </Form.Item>
                    </div>
                  </Form.Item>
                }
              </Form.Item>
            </Col>
            <Col span={2}></Col>
            <Col span={11} style={{ marginTop: '20px' }}>
              <Form.Item
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                label="Enable Puja Ritual update"
                name={['enable_puja_faq']}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.enable_puja_faq !== currentValues.enable_puja_faq
                }
              >
                {({ getFieldValue }) =>
                  <>
                    <Form.Item
                      label='Puja Ritual update'
                      rules={[{ required: true, message: 'Please select media type!' }]}
                    >
                      <div style={{ border: '1px dashed #e0dcdc', padding: '10px' }}>
                        <Form.Item

                          label='Title'
                          name={['puja_faq', 'title']}
                          labelCol={{ span: 24 }}
                          wrapperCol={{ span: 24 }}
                          rules={[
                            { required: getFieldValue("enable_puja_faq"), message: 'Please input FAQ Title' },
                          ]}
                        >
                          <Input placeholder='Enter FAQ Title' disabled={!getFieldValue("enable_puja_faq")} />
                        </Form.Item>
                        <Form.Item
                          label='Description'
                          name={['puja_faq', 'description']}
                          labelCol={{ span: 24 }}
                          wrapperCol={{ span: 24 }}
                          rules={[
                            { required: getFieldValue("enable_puja_faq"), message: 'Please input FAQ Description' },
                          ]}
                        >
                          <TextArea placeholder='Enter FAQ Description' disabled={!getFieldValue("enable_puja_faq")} />
                        </Form.Item>
                        <Form.Item
                          label='CTA Text'
                          name={['puja_faq', 'l2_cta_text']}
                          labelCol={{ span: 24 }}
                          wrapperCol={{ span: 24 }}
                          rules={[
                            { required: getFieldValue("enable_puja_faq"), message: 'Please input FAQ Cta Text' },
                          ]}
                        >
                          <Input placeholder='Enter FAQ CTA Text' disabled={!getFieldValue("enable_puja_faq")} />
                        </Form.Item>

                        <Form.Item
                          label='Media Url'
                          name={['puja_faq', 'media_urls']}
                          labelCol={{ span: 24 }}
                          wrapperCol={{ span: 24 }}
                          valuePropName='fileList'
                          rules={[
                            {
                              min: getFieldValue("enable_puja_faq") ? 1 : 0,
                              max: 1,
                              validator: async (rules: any, values: any) => {
                                if (values?.length < rules.min) {
                                  return Promise.reject('Please upload image ')
                                }
                              },
                            },
                          ]}
                        >
                          <S3Upload
                            cdnUrl={YODA_CDN_URL}
                            preSignUrl={PUJA_PRESIGN_URL}
                            min={getFieldValue("enable_puja_faq") ? 1 : 0}
                            max={10}
                            disabled={!getFieldValue("enable_puja_faq")}
                          />
                        </Form.Item>
                      </div>
                    </Form.Item>
                  </>
                }
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Form >
      <Col span={24}>
        <LabelRightAlign>
          <Space>
            <Button type='primary' onClick={() => navigate("/monetisation/puja/temples")}>
              Cancel
            </Button>
            {currentStep > 0 && (
              <Button type='primary' onClick={previousHandler}>
                Previous
              </Button>
            )}
            {isEditJourney && currentStep !== 1 && (
              <Button type='primary' onClick={submitFormHandler}>
                Update
              </Button>
            )}
            {currentStep < 3 && currentStep !== 1 && !isEditJourney ? (
              <Button type='primary' onClick={submitFormHandler}>
                Submit
              </Button>
            ) : (
              currentStep !== 2 && (
                <Button type='primary' onClick={nextHandler}>
                  Next
                </Button>
              )
            )}
          </Space>
        </LabelRightAlign>
        {
          initialStoreData &&
          <Button type='primary' onClick={handleShowFilters} icon={showFilters ? <CloseCircleOutlined /> : <FilterOutlined />}>
            {showFilters ? 'Hide filters' : 'Show Filters'}
          </Button>
        }
        {showFilters &&
          <div style={{ marginTop: '20px' }}>
            <FilterForm storeInfo={{ storeId: initialStoreData?.store_id, storeName: initialStoreData?.title }} />
          </div>
        }
      </Col>

    </div >
  )
}
export default React.memo(StoreForm)